import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { TApiApplication, TPageConfiguration } from '../../../types.ts';
import { COVENANTS_DATE_TIME_FORMAT } from '~/modules/covenants/constants.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { clientStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/styles.ts';
import { formatDate } from '~/modules/covenants/utils.ts';

interface ICreateApplicationModalProps {
  open: boolean;
  onClose: () => void;
  onCreateNew: () => void;
  onEditExisting: (appId: string) => void;
  activeApplications: TApiApplication[];
  activeApplicationsPage: number;
  activeApplicationsPageConfiguration: TPageConfiguration;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

export const CreateApplicationModal: React.FC<ICreateApplicationModalProps> = ({
  open,
  onClose,
  onCreateNew,
  onEditExisting,
  activeApplications,
  activeApplicationsPage,
  activeApplicationsPageConfiguration,
  isLoading,
  onPageChange,
}) => {
  const pageSize = Math.max(activeApplicationsPageConfiguration.size, 1);
  const totalPages = Math.ceil(activeApplicationsPageConfiguration.totalElements / pageSize);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: clientStyles.createDialogPaper } }}
    >
      <DialogTitle sx={clientStyles.createTitle}>
        <Box sx={clientStyles.createHeaderContent}>
          <Typography component="span" sx={clientStyles.createTitleText}>
            Створення заявки
          </Typography>
          <Typography variant="body2" sx={clientStyles.createSubtitle}>
            Створіть нову заявку або оберіть існуючу
          </Typography>
        </Box>

        <IconButton aria-label="Закрити" onClick={onClose} sx={clientStyles.createCloseButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={clientStyles.createContent}>
        {activeApplicationsPageConfiguration.totalElements > 0 && (
          <Box sx={[commonStyles.loadingWrap, clientStyles.createBody]}>
            <Divider sx={clientStyles.createDivider} />

            <Box sx={clientStyles.createList}>
              {activeApplications.map((app) => (
                <Box key={app.id} sx={clientStyles.createListRow}>
                  <Box sx={clientStyles.createListMeta}>
                    <Typography variant="subtitle2" sx={clientStyles.createListTitle}>
                      {app.localId != null ? `Заявка №${app.localId}` : app.displayName}
                    </Typography>
                    <Typography variant="body2" sx={clientStyles.createListDate}>
                      {app.createdAt ? formatDate(app.createdAt, COVENANTS_DATE_TIME_FORMAT) : '-'}
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={() => onEditExisting(app.id)}
                    disabled={isLoading}
                    endIcon={<ArrowForwardIcon />}
                    sx={clientStyles.createListBtn}
                  >
                    Переглянути
                  </Button>
                </Box>
              ))}
            </Box>

            {isLoading && (
              <Box sx={commonStyles.loadingMask}>
                <CircularProgress size={28} />
              </Box>
            )}

            {totalPages > 1 && (
              <Box sx={clientStyles.createPaginationWrap}>
                <TablePaginationControls
                  page={activeApplicationsPage}
                  rowsPerPage={pageSize}
                  totalElements={activeApplicationsPageConfiguration.totalElements}
                  isLoading={isLoading}
                  showSummary={false}
                  justifyContent="center"
                  onPageChange={onPageChange}
                  onRowsPerPageChange={() => {}}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={clientStyles.createActions}>
        <Button onClick={onClose} sx={clientStyles.createCancelBtn}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          onClick={onCreateNew}
          disabled={isLoading}
          sx={clientStyles.createBtn}
        >
          Створити нову заявку
        </Button>
      </DialogActions>
    </Dialog>
  );
};
